/* eslint-disable no-invalid-this */
import { Controller } from 'stimulus';

class ValidationController extends Controller {
  static targets = ['validates'];

  connect() {
    this.element.setAttribute('novalidate', true);
    this.element.addEventListener('blur', this.onBlur, true);
    this.element.addEventListener('submit', this.onSubmit);
  }

  disconnect() {
    this.element.removeEventListener('blur', this.onBlur);
    this.element.removeEventListener('submit', this.onSubmit);
  }

  /**
   * Event listener to capture focus being transitioned away from an input.
   * If triggering element has the data attribute `data-target="validation.validates"
   * then pass the element to, and invoke, `this.validateField`.
   * @param {Event} e HTMLEvent 'blur' triggered by losing focus on a form input
   * @returns {void}
   */
  onBlur = (e) => {
    if (!this.validatesTargets.includes(e.target)) {
      return;
    }

    if (this.shouldValidateField(e.target)) {
      this.validateField(e.target);
    }
  };

  /**
   * Event listener to capture form submission. Invokes, and captures result of,
   * `this.validateForm`. If validation success, dispatch 'form-validation-success'
   * custom event (useful for other controllers). Else prevent form submission,
   * set focus on the first invalid field, and dispatch `form-validation-failure`
   * custom event.
   * @param {*} e HTMLEvent 'submit' triggered by submission of the form
   * @returns {void}
   */
  onSubmit = (e) => {
    const validationStatus = this.validateForm() ? 'success' : 'failure';
    const validationEvent = new CustomEvent(
      `form-validation-${validationStatus}`,
      {
        detail: {
          triggerEvent: e,
        },
      }
    );

    if (!this.validateForm()) {
      e.preventDefault();
      this.firstInvalidField.focus();
    }

    document.dispatchEvent(validationEvent);
  };

  /**
   * Iterates through all validation fields (elements with `data-target="validation.validates"`),
   * if any field should be validated (`this.shouldValidateField(field)`) and
   * is inValid (`this.validateField(field)`) returns false. Else if all fields
   * are valid, returns true.
   * @returns {Boolean} Whether or not the form is valid
   */
  validateForm() {
    return this.validatesTargets.every(
      (field) =>
        !(this.shouldValidateField(field) && !this.validateField(field))
    );
  }

  /**
   * Given a form field, if the field should be validated (verified by invoking
   * `this.shouldValidateField(field)`), checks if the field is valid by
   * invoking `field.checkValidity()`.
   * Invokes `this.refreshErrorForInvalidField` with the field and whether the
   * field is valid or not
   * @param {HTMLElement} field Form input field
   * @returns {Boolean} Whether or not the given field is valid
   */
  validateField(field) {
    const isValid = field.checkValidity();

    // `data-external-validation` signifies this controller should not add classnames or errors
    if (!field.dataset.hasOwnProperty('externalValidation')) {
      this.refreshErrorForInvalidField(field, isValid);
    }

    return isValid;
  }

  /**
   * Given a form field, returns the suggested parent container class name based
   * on `field.type`.
   * @param {HTMLElement} field Form input field
   * @returns {String} Class name suggestion for field's parent container where
   *                   name is one of: `['.gzl-Textarea', '.gzl-Select', '.gzl-Input']`
   */
  parentClass(field) {
    switch (field.type) {
    case 'textarea':
      return '.gzl-Textarea';
    case 'select-one':
      return '.gzl-Select';
    case 'select-multiple':
      return '.gzl-Select';
    default:
      return '.gzl-Input';
    }
  }

  /**
   * Given a form field checks if this field should be validated. Checks:
   * 1. If the field container is hidden or not
   * 2. If the field is disabled
   * 3. If the field is _not_ a button
   * 4. For a file field: if the file is not uploaded or if it's marked invalid
   * If all 4 are true, returns true, else returns false.
   * @param {HTMLElement} field Form input field
   * @returns {Boolean} Whether or not this field should be validated
   */
  shouldValidateField(field) {
    const container = field.closest('.gzl-FormField') || field.closest('.pro-Modal-section');
    let containerVisible = container?.style?.display !== 'none';
    let fileUploaded = container?.querySelector('.pro-Modal-fileIndicator')?.innerHTML !== 'No file selected';

    if (containerVisible && container?.classList.contains('u-displayNone')) {
      containerVisible = false;
    }

    return (
      containerVisible &&
      !field.disabled &&
      (field.type === 'file' ? (!fileUploaded || field.parentElement.classList.contains('is-invalid')) : true) &&
      !['submit', 'button'].includes(field.type)
    );
  }

  /**
   * Removes the given field's error message. Then, if an error is present on
   * the field (`isValid === true`), adds an error message.
   * Invokes `addOrRemoveInvalidClass` with the field's parent container and
   * whether or not the field is valid.
   * @param {HTMLElement} field Form input field
   * @param {Boolean} isValid Whether or not the given field is valid
   * @returns {void}
   */
  refreshErrorForInvalidField(field, isValid) {
    const parentClass = this.parentClass(field);

    this.removeExistingErrorMessage(field);
    this.addOrRemoveInvalidClass(field.closest(parentClass), isValid);

    if (!isValid) {
      this.showErrorForInvalidField(field);
    }
  }

  /**
   * Given a form input field, remove any existing error message element siblings
   * (denoted by class name `'.u-formError'`).
   * @param {HTMLElement} field Form input field
   * @returns {void}
   */
  removeExistingErrorMessage(field) {
    const parentClass = this.parentClass(field);
    const fieldContainer = field.closest(parentClass);
    const fieldContainerParent = field.closest(parentClass).parentNode;

    if (!fieldContainerParent) {
      return;
    }

    const existingErrorMessageElement =
      fieldContainerParent.querySelector('.u-formError');

    if (existingErrorMessageElement) {
      fieldContainerParent.removeChild(existingErrorMessageElement);
      this.addOrRemoveInvalidClass(fieldContainer, true);
    }
  }

  /**
   * Given an html element, adds or removes class `is-invalid` based on whether
   * the address is valid or not.
   * @param {HTMLElement} element Element on which to add or remove class
   * @param {Boolean} isValid Whether or not the address is valid
   * @returns {void}
   */
  addOrRemoveInvalidClass(element, isValid) {
    const addOrRemove = isValid ? 'remove' : 'add';
    // IE11 workaround by using `addOrRemove` instead of `.toggle()`
    element.classList[addOrRemove]('is-invalid');
  }

  /**
   * Given a form input field, add sibling element displaying the error message
   * after the field.
   * @param {HTMLElement} field Form input field
   * @returns {void}
   */
  showErrorForInvalidField(field) {
    const parentClass = this.parentClass(field);
    const input = field.closest(parentClass);

    input.insertAdjacentHTML('afterend', this.buildFieldErrorHtml(field));
  }

  /**
   * Given a form input field, build a `p` tag with class name `u-formError` and
   * a text content of the field's validation message.
   * If the field has a `data-custom-error-message` field, inserts the custom
   * error message.
   * @param {HTMLElement} field Form input field
   * @returns {String} `p` tag with an error message as textContent
   */
  buildFieldErrorHtml(field) {
    const customErrorMessage = field.dataset.customErrorMessage;
    const validationMessage = field.validationMessage;

    return `<p class="u-formError">${
      customErrorMessage ? customErrorMessage : validationMessage
    }</p>`;
  }

  get firstInvalidField() {
    return this.validatesTargets.find(
      (field) => this.shouldValidateField(field) && !field.checkValidity()
    );
  }
}

export default ValidationController;
/* eslint-enable no-invalid-this */
